// One file of a folder in a big window over the folder, without downloading it. Ported from Space
// (C:\Github\Space\app\src\components\FilePreview.tsx).
//   image, video, audio, PDF    the browser shows the read URL itself (an image: its small picture's URL)
//   Word, Excel, PowerPoint     downloaded into the page and drawn by a library, loaded only then; Excel read in a
//                               worker (sheetWorker.ts)
//   Markdown, CSV, text         their first 2 MB downloaded, shown as formatted text or a table; HTML and XML as text
//   zip                         its list of files: only the end of the zip is read
//   anything else               "No preview" + Download
// ← → the previous / next file, in the folder's order. Esc closes the preview only: the folder stays. A PDF
// clicked keeps every key (the browser's viewer, a frame): the header says to click it first.
// Nothing is sent anywhere: the bytes come from Blob with a read URL, and are drawn in this page.
// DOMPurify is in the app already (the notes); the other libraries load with the first such file (import()).
import { useQuery } from '@tanstack/react-query';
import DOMPurify, { type Config } from 'dompurify';
import { isInlineSafe, type FileUrlAnswer, type FolderFile } from '@pathonline/shared';
import { Component, memo, useEffect, useId, useMemo, useRef, useState, type DragEvent, type ReactNode, type SyntheticEvent } from 'react';
import { createPortal } from 'react-dom';
import { getJson, HttpError } from '../api.ts';
import { extOf, fileMeta, fmtSize, nodeLinks, readProblem, type FileLinks } from './model.ts';
import { askWorker, cellDate, columnName, decodeText, HEAD_ONLY, linkKind, PARSE_LIMIT, parseCsv, previewKind, renameFonts, safeCss, safeSvgRef, srcAllowed, STREAMED, SVG_REF_ATTRS, TEXT_LIMIT, type PreviewKind } from './preview.ts';
import type { SheetAsk } from './sheetWorker.ts';
import { DRAWN_LIMIT, listZip, UNPACK_LIMIT, unpacksSmall, zipSummary, zipTree, type ZipNode } from './zip.ts';

interface Props {
  nodeId: string;
  /** The folder's files, in its order: ← → go through them. */
  files: readonly FolderFile[];
  index: number;
  onIndex: (index: number) => void;
  onClose: () => void;
  /** Where Download, ↗ and the read URL point: a member's routes by default; a shared link's page passes its own. */
  links?: FileLinks;
}

/** A small picture that fails to load hides: the extension under it shows again. */
const hideBroken = (e: SyntheticEvent<HTMLImageElement>) => {
  e.currentTarget.hidden = true;
};

/** A file's badge (the folder's list, the preview's header): its extension; an image's small picture over it. */
export function FileBadge({ name, thumb, onClick }: { name: string; thumb?: string; onClick?: () => void }) {
  return (
    <span className="fm-ext" aria-hidden="true" onClick={onClick}>
      {extOf(name) || '·'}
      {/* Keyed: a new URL (the list read again, the next file) is a new <img>, not one hidden by a failed one. */}
      {thumb && <img key={thumb} src={thumb} alt="" loading="lazy" onError={hideBroken} />}
    </span>
  );
}

/**
 * Where the focus is, for the window's keys. 'lost': on the page, or on a button turned off; no key reaches the
 * window. 'frame': in a PDF's frame; the browser's viewer keeps every key, Esc too. Else 'window'.
 */
export function focusAt(win: Node, active: Element | null): 'window' | 'frame' | 'lost' {
  if (!active || !win.contains(active) || (active as HTMLButtonElement).disabled) return 'lost';
  return active.tagName === 'IFRAME' ? 'frame' : 'window';
}

export function FilePreview({ nodeId, files, index, onIndex, onClose, links }: Props) {
  const ref = useRef<HTMLDialogElement>(null);
  const header = useRef<HTMLElement>(null);
  /** The press started on the backdrop (a drag out of the window is not a click beside it). */
  const down = useRef(false);
  const titleId = useId();
  // Where the focus was (Open, a file's name): it goes back there when the preview closes.
  const [back] = useState(() => (typeof document === 'undefined' ? null : (document.activeElement as HTMLElement | null)));
  // The focus is in a PDF's frame: the header says how to get Esc and ← → back.
  const [inFrame, setInFrame] = useState(false);
  const file = files[index];

  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
    // The window itself, not its first button: ← → work at once.
    d?.focus();
  }, []);
  useEffect(() => () => back?.focus?.(), [back]);
  // The page loses the focus to a frame (blur) and gets it back (focus). Read once the focus has moved.
  useEffect(() => {
    let timer = 0;
    const check = () => {
      clearTimeout(timer);
      timer = window.setTimeout(() => setInFrame(!!ref.current && focusAt(ref.current, document.activeElement) === 'frame'));
    };
    window.addEventListener('blur', check);
    window.addEventListener('focus', check);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('blur', check);
      window.removeEventListener('focus', check);
    };
  }, []);
  // Another file, or fewer files: the focused button may be gone (↗ is not there for every file; ‹ › go with one
  // file left) or turned off (‹ › at either end). The focus goes back to the window, or ← → would stop.
  useEffect(() => {
    const d = ref.current;
    if (d && focusAt(d, document.activeElement) === 'lost') d.focus();
  }, [index, files.length]);

  if (!file) return null;
  const kind = previewKind(file.name, file.type);
  const link = links ?? nodeLinks(nodeId);
  const download = link.file(file.id, true);
  const go = (step: number) => {
    const next = index + step;
    if (next >= 0 && next < files.length) onIndex(next);
  };
  // Nothing dropped on the preview is uploaded, and the browser does not open it in the tab either.
  const noDrop = (e: DragEvent) => {
    e.stopPropagation();
    e.preventDefault();
    e.dataTransfer.dropEffect = 'none';
  };

  const win = (
    <dialog
      ref={ref}
      className="fp-win"
      aria-labelledby={titleId}
      tabIndex={-1}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          // Handled: the folder under it stays open (FolderModal skips a handled Escape).
          e.preventDefault();
          onClose();
          return;
        }
        // Only from the window or its header. Anything focused in the file keeps its arrows: a video seeks, a sheet scrolls.
        if (e.target !== e.currentTarget && !header.current?.contains(e.target as Node)) return;
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        e.preventDefault();
        go(e.key === 'ArrowLeft' ? -1 : 1);
      }}
      onMouseDown={(e) => (down.current = e.target === e.currentTarget)}
      onClick={(e) => {
        if (down.current && e.target === e.currentTarget) onClose();
      }}
      onDragEnter={noDrop}
      onDragOver={noDrop}
      onDrop={noDrop}
    >
      <header ref={header} className="fp-hd">
        <FileBadge name={file.name} thumb={file.thumb} />
        <div className="fp-title">
          <h2 id={titleId} title={file.name}>
            {file.name}
          </h2>
          <p className="fp-meta">
            {fileMeta(file)}
            {files.length > 1 && ` · ${index + 1} / ${files.length}`}
          </p>
        </div>
        {/* A click on the header takes the focus back from the PDF: the window has it, and its keys work again. */}
        {inFrame && (
          <span className="fp-tip" role="status">
            Esc, ← →: click this bar first
          </span>
        )}
        {files.length > 1 && (
          <span className="fp-nav">
            <button type="button" className="fp-btn" disabled={index === 0} onClick={() => go(-1)} aria-label="Previous file (←)" title="Previous file (←)">
              ‹
            </button>
            <button type="button" className="fp-btn" disabled={index === files.length - 1} onClick={() => go(1)} aria-label="Next file (→)" title="Next file (→)">
              ›
            </button>
          </span>
        )}
        {/* New tabs, as the folder's links: the server answers 302; a refusal shows its short page there. */}
        <a className="fp-btn" href={download} target="_blank" rel="noopener noreferrer" aria-label={`Download ${file.name}`} title="Download">
          ⬇
        </a>
        {isInlineSafe(file.type) && (
          <a className="fp-btn" href={link.file(file.id)} target="_blank" rel="noopener noreferrer" aria-label={`Open ${file.name} in a new tab`} title="Open in a new tab">
            ↗
          </a>
        )}
        <button type="button" className="fp-btn" onClick={onClose} aria-label="Close the preview (Esc)" title="Close (Esc)">
          ×
        </button>
      </header>
      <div className="fp-body">
        <Guard key={file.id} download={download}>
          <Body nodeId={nodeId} file={file} kind={kind} urlApi={link.url(file.id)} download={download} />
        </Guard>
      </div>
    </dialog>
  );
  // In <body>, out of the page it was opened on: no rule of that page (.gantt-page .panel h2) reaches the file.
  // React's events still go up through the folder's backdrop. No DOM in the tests: the window itself.
  return typeof document === 'undefined' ? win : createPortal(win, document.body);
}

/**
 * One file drawn in the page itself, not in a window: an open topic's full description (apply/TopicPage.tsx).
 * The same views and the same cleaning as the preview; `links` say where its read URL and Download are.
 */
export function FileView({ nodeId, file, links }: { nodeId: string; file: FolderFile; links: FileLinks }) {
  const download = links.file(file.id, true);
  return (
    <div className="fp-body fp-inline">
      <Guard key={file.id} download={download}>
        <Body nodeId={nodeId} file={file} kind={previewKind(file.name, file.type)} urlApi={links.url(file.id)} download={download} />
      </Guard>
    </div>
  );
}

/**
 * A view that cannot draw its file says so: one that throws (a file no one expected), and one whose library
 * fails (useDrawn, SheetView throw then). Uncaught, React would empty the whole page.
 */
class Guard extends Component<{ download: string; children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  override render() {
    if (!this.state.failed) return this.props.children;
    return <Message title="Could not show the file" download={this.props.download} />;
  }
}

/**
 * A read URL works 2 hours (the server's VIEW_MINUTES). Used again for 90 minutes: going back reuses it and
 * the browser's copy of the file. Older, the next opening asks for a new one and waits for it.
 */
const URL_FRESH = 90 * 60_000;
/** A video or a sound reads its URL on while it plays (a seek, play after a pause): reused only while 90 minutes are left. */
const MEDIA_FRESH = 30 * 60_000;

/** Why a read failed, in words: no answer at all is a TypeError from fetch (the connection, or the storage refusing this site). */
function errorText(e: unknown): string {
  if (e instanceof TypeError) return readProblem(0);
  return e instanceof Error ? e.message : '';
}

/** Word, Excel, PowerPoint: zips their library unpacks whole, within these (unpacksSmall). */
const UNPACKED: Partial<Record<PreviewKind, typeof UNPACK_LIMIT>> = { docx: DRAWN_LIMIT, xlsx: UNPACK_LIMIT, pptx: DRAWN_LIMIT };

/**
 * The file from its read URL: whole, or its first `head` bytes (a Range). A refusal is an HttpError: not asked
 * again (retryRule). `unpacked`: a zip that unpacks to more (unpacksSmall) is refused so too, before its library
 * unpacks it.
 */
async function readBytes(url: string, signal: AbortSignal, unpacked?: typeof UNPACK_LIMIT, head?: number): Promise<ArrayBuffer> {
  const res = await fetch(url, { signal, headers: head ? { Range: `bytes=0-${head - 1}` } : undefined });
  if (!res.ok) throw new HttpError(res.status, readProblem(res.status));
  // A server that ignores Range sends everything: only its first `head` bytes are downloaded.
  if (head && res.status !== 206) return firstBytes(res, head);
  const bytes = await res.arrayBuffer();
  if (unpacked && !(await unpacksSmall(bytes, unpacked, signal))) throw new HttpError(413, 'Too large to preview here once unpacked. Download it and open it on your computer.');
  return bytes;
}

/** The first `n` bytes of an answer (all of a shorter one). Then its download stops. */
async function firstBytes(res: Response, n: number): Promise<ArrayBuffer> {
  const out = new Uint8Array(n);
  let at = 0;
  const reader = res.body?.getReader();
  while (reader && at < n) {
    const r = await reader.read();
    if (r.done) break;
    const part = r.value.subarray(0, n - at);
    out.set(part, at);
    at += part.length;
  }
  reader?.cancel().catch(() => undefined);
  return out.buffer.slice(0, at);
}

/**
 * The file itself, as its kind is shown. memo: the folder under the preview draws again on every upload
 * progress tick; its props stay the same then, so the file (a sheet's thousands of cells) is not drawn again.
 * `urlApi`: where its read URL is asked (FileLinks.url); `download`: the file's Download (FileLinks.file).
 */
const Body = memo(function Body({ nodeId, file, kind, urlApi, download }: { nodeId: string; file: FolderFile; kind: PreviewKind | null; urlApi: string; download: string }) {
  const streamed = !!kind && STREAMED.includes(kind);
  // Text, CSV, Markdown: only their beginning is read, whatever their size. Over TEXT_LIMIT, one byte more tells
  // Truncated; a smaller one is read whole (an empty file answers a Range with 416).
  const headOnly = !!kind && HEAD_ONLY.includes(kind);
  const head = headOnly && file.size > TEXT_LIMIT ? TEXT_LIMIT + 1 : undefined;
  const tooBig = !!kind && !streamed && !headOnly && file.size > PARSE_LIMIT;
  // An image's small picture is a read URL for the whole image already: no second download. Failing, the preview asks for its own.
  const [thumbFailed, setThumbFailed] = useState(false);
  const [broken, setBroken] = useState(false);
  const thumb = kind === 'image' && !thumbFailed ? file.thumb : undefined;

  // The read URL (URL_FRESH, MEDIA_FRESH). Never asked again while shown, not even on focus or a new connection: a
  // new URL would reload a playing video from 0:00. Kept 30 minutes after closing.
  const url = useQuery({
    queryKey: ['fileUrl', nodeId, file.id],
    queryFn: () => getJson<FileUrlAnswer>(urlApi).then((r) => r.url),
    enabled: !!kind && !tooBig && !thumb,
    staleTime: kind === 'video' || kind === 'audio' ? MEDIA_FRESH : URL_FRESH,
    gcTime: 30 * 60_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  // An old URL being asked again is not used meanwhile: it may have ended, and the view would fail on it.
  const readUrl = url.isFetching && url.isStale ? undefined : url.data;
  // Word, Excel, PowerPoint are read whole, Markdown, CSV and text their beginning (head), and drawn here. Leaving the
  // file stops the download (signal).
  const data = useQuery({
    queryKey: ['fileData', nodeId, file.id],
    queryFn: ({ signal }) => readBytes(readUrl!, signal, kind ? UNPACKED[kind] : undefined, head),
    enabled: !!readUrl && !streamed && !tooBig,
    staleTime: Infinity,
    gcTime: 60_000,
  });

  const say = (title: string, body?: string) => <Message title={title} body={body} download={download} />;
  if (!kind) return say('No preview for this file');
  if (tooBig) return say(`Too large to preview here (over ${PARSE_LIMIT / 1024 / 1024} MB)`);
  if (broken) return say('Could not show the file');
  if (url.isError || data.isError) return say('Could not show the file', errorText(url.error ?? data.error));
  const src = thumb ?? readUrl;
  if (!src) return <Loading />;

  switch (kind) {
    case 'image':
      // Only ever in <img>: an SVG runs nothing there.
      return (
        <div className="fp-center fp-img">
          <img src={src} alt={file.name} onError={() => (thumb ? setThumbFailed(true) : setBroken(true))} />
        </div>
      );
    case 'video':
      return (
        <div className="fp-center fp-video">
          <video src={src} controls autoPlay onError={() => setBroken(true)} />
        </div>
      );
    case 'audio':
      return (
        <div className="fp-center">
          <audio src={src} controls autoPlay onError={() => setBroken(true)} />
        </div>
      );
    case 'pdf':
      return <iframe className="fp-pdf" src={src} title={file.name} />;
    case 'zip':
      return <ZipView url={src} size={file.size} failed={(why) => say('Could not list the zip', why)} />;
  }

  if (!data.data) return <Loading />;
  switch (kind) {
    case 'docx':
      return <DocxView data={data.data} />;
    case 'xlsx':
      return <SheetView nodeId={nodeId} fileId={file.id} data={data.data} />;
    case 'pptx':
      return <PptxView data={data.data} />;
    case 'markdown':
      return <MarkdownView data={data.data} />;
    case 'csv':
      return <CsvView data={data.data} />;
    case 'text':
      return <TextView data={data.data} />;
  }
});

/** Under a cut text, CSV or Markdown file. */
function Truncated({ data }: { data: ArrayBuffer }) {
  return data.byteLength > TEXT_LIMIT ? <p className="fp-note">Showing the beginning only (first {TEXT_LIMIT / 1024 / 1024} MB).</p> : null;
}

function Loading() {
  return (
    <div className="fp-center">
      <p className="fp-wait" role="status">
        Loading…
      </p>
    </div>
  );
}

/** Why there is nothing to see, and Download. */
function Message({ title, body = 'Download it and open it on your computer.', download }: { title: string; body?: string; download: string }) {
  return (
    <div className="fp-center">
      <div className="fp-msg">
        <span className="fp-msg-icon" aria-hidden="true">
          ?
        </span>
        <p className="fp-msg-t">{title}</p>
        {body && <p className="fp-msg-b">{body}</p>}
        <a className="btn" href={download} target="_blank" rel="noopener noreferrer">
          ⬇ Download
        </a>
      </div>
    </div>
  );
}

/**
 * Runs `draw` (a library loaded on demand) into the element. Its result, if any, cleans up. A draw that fails
 * throws here, while rendering: Guard shows "Could not show the file".
 */
function useDrawn(draw: (el: HTMLDivElement) => Promise<void | (() => void)>, deps: unknown[]) {
  const el = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<'loading' | 'done' | { failed: unknown }>('loading');
  useEffect(() => {
    let cleanup: void | (() => void);
    let alive = true;
    setState('loading');
    if (el.current)
      draw(el.current)
        .then((c) => {
          cleanup = c;
          if (alive) setState('done');
          else c?.();
        })
        .catch((e: unknown) => alive && setState({ failed: e }));
    return () => {
      alive = false;
      cleanup?.();
    };
  }, deps);
  if (typeof state === 'object') throw state.failed;
  return { el, loading: state === 'loading' };
}

/**
 * Links in a drawn file (linkKind): the web ones open in a new tab; "#name" jumps inside the file when
 * `find` is given; every other link loses its target.
 */
function keepSafeLinks(root: ParentNode, find?: (id: string) => Element | null) {
  for (const a of root.querySelectorAll('a')) {
    const href = a.getAttribute('href') ?? '';
    const kind = linkKind(href);
    if (kind === 'web') {
      a.setAttribute('target', '_blank');
      a.setAttribute('rel', 'noopener noreferrer');
      continue;
    }
    a.removeAttributeNS('http://www.w3.org/1999/xlink', 'href');
    if (kind === 'anchor' && find)
      a.addEventListener('click', (e) => {
        e.preventDefault();
        find(href.slice(1))?.scrollIntoView({ block: 'start' });
      });
    else a.removeAttribute('href');
  }
}

/** Tags a drawn file never keeps: forms and their fields, dialogs, frames, plugins, and SVG's ways to load another document. */
const FORBID_TAGS = ['style', 'form', 'input', 'button', 'textarea', 'select', 'option', 'dialog', 'iframe', 'object', 'embed', 'use', 'feimage'];
/** Attributes that load something besides src (a srcset, a table's background, a video's poster), or send something (ping). */
const FORBID_ATTR = ['srcset', 'background', 'poster', 'ping'];

/**
 * Pictures and media load only from data:, blob: or another site (srcAllowed); inline CSS loads nothing (safeCss),
 * nor does SVG's fill="url(…)" and the like (safeSvgRef: Word copies a shape's colour into fill as written).
 */
function keepSafeSources(root: ParentNode) {
  for (const el of root.querySelectorAll('img, image, video, audio, source, track')) {
    for (const name of ['src', 'href', 'xlink:href']) {
      const v = el.getAttribute(name);
      if (v !== null && !srcAllowed(v, location.origin)) el.removeAttribute(name);
    }
  }
  for (const el of root.querySelectorAll('[style]')) el.setAttribute('style', safeCss(el.getAttribute('style') ?? ''));
  for (const name of SVG_REF_ATTRS) for (const el of root.querySelectorAll(`[${name}]`)) el.setAttribute(name, safeSvgRef(el.getAttribute(name) ?? ''));
}

/** A drawn file made safe where it is: DOMPurify (the lists above, and `extra`), then its links and what it loads. */
function clean(root: HTMLElement, extra: Config, find?: (id: string) => Element | null) {
  DOMPurify.sanitize(root, { FORBID_TAGS, FORBID_ATTR, ...extra, IN_PLACE: true });
  keepSafeLinks(root, find);
  keepSafeSources(root);
}

/**
 * DOMPurify's own pattern for attribute values, with blob: added: PowerPoint's videos and sounds. Every
 * attribute value must pass it (fill="rgb(…)" too), so a narrower one would empty the slides.
 */
const WITH_BLOB = /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp|matrix|blob):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i;

/**
 * Word's pages, in a shadow root of their own: the file's CSS styles only them (safeCss takes :host away).
 * Cut to the box (contain), here and again on .fp-body, which no CSS in here can reach. A page wider than the
 * box (a phone) starts at its left edge ("safe"): centred, its left part would be out of reach of the scroll.
 */
const DOCX_CSS = `:host { display: block; contain: paint; }
.docx-wrapper { background: transparent; padding: 24px 12px; align-items: safe center; }
.docx-wrapper > section.docx { box-shadow: 0 4px 24px rgba(0, 0, 0, .14); margin-bottom: 24px; }`;

/** Word's text boxes: HTML inside SVG (<foreignObject>), kept and cleaned as the rest. */
const TEXT_BOXES: Config = { ADD_TAGS: ['foreignObject'], HTML_INTEGRATION_POINTS: { foreignobject: true, 'annotation-xml': true } };

/** Word files drawn so far: each one's embedded fonts get names of their own (docx1-0, docx2-0 …). */
let docxDrawn = 0;

/**
 * Word's embedded fonts (@font-face in its CSS), for the whole page: in a shadow root @font-face does nothing.
 * Each under a new name, changed in the file's CSS and styles too, so a font the file names as the app's own
 * ("Segoe UI") changes nothing outside. Returns them, to take away on leaving the file.
 */
function embedFonts(styles: HTMLElement, body: HTMLElement): FontFace[] {
  const n = ++docxDrawn;
  const names = new Map<string, string>();
  const fonts: FontFace[] = [];
  for (const css of styles.querySelectorAll('style')) {
    if (!/@font-face/i.test(css.textContent ?? '')) continue;
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(css.textContent ?? '');
    const faces = [...sheet.cssRules].filter((rule) => rule instanceof CSSFontFaceRule);
    for (const { style } of faces) {
      const family = style.getPropertyValue('font-family').trim().replace(/^(['"])(.*)\1$/, '$2').toLowerCase();
      if (!names.has(family)) names.set(family, `docx${n}-${names.size}`);
      const font = new FontFace(names.get(family)!, style.getPropertyValue('src'), {
        weight: style.getPropertyValue('font-weight') || 'normal',
        style: style.getPropertyValue('font-style') || 'normal',
      });
      // A font that does not load: the text keeps a font of the browser's, and the console stays quiet.
      font.loaded.catch(() => undefined);
      document.fonts.add(font);
      fonts.push(font);
    }
    if (faces.length && faces.length === sheet.cssRules.length) css.remove();
  }
  for (const css of styles.querySelectorAll('style')) css.textContent = renameFonts(css.textContent ?? '', names);
  for (const el of body.querySelectorAll('[style]')) el.setAttribute('style', renameFonts(el.getAttribute('style') ?? '', names));
  return fonts;
}

function DocxView({ data }: { data: ArrayBuffer }) {
  const { el, loading } = useDrawn(
    async (host) => {
      const { renderAsync } = await import('docx-preview');
      const root = host.shadowRoot ?? host.attachShadow({ mode: 'open' });
      // Drawn away from the page: the file's CSS is read (and could load something) only once cleaned and put in.
      const styles = document.createElement('div');
      const body = document.createElement('div');
      // Pictures as data: URLs. No altChunks: those are raw HTML parts, drawn in an iframe on this site.
      await renderAsync(data, body, styles, { inWrapper: true, ignoreLastRenderedPageBreak: true, experimental: true, useBase64URL: true, renderAltChunks: false });
      // Someone's file: only plain HTML and SVG stay. No script, no event handler, no javascript: link, nothing
      // loaded from this site.
      clean(body, TEXT_BOXES, (id) => root.getElementById(id));
      // docx-preview writes the file's styles into CSS text: a font name can carry rules of its own.
      for (const css of styles.querySelectorAll('style')) css.textContent = safeCss(css.textContent ?? '');
      const fonts = embedFonts(styles, body);
      const own = document.createElement('style');
      own.textContent = DOCX_CSS;
      root.replaceChildren(styles, body, own);
      return () => fonts.forEach((font) => document.fonts.delete(font));
    },
    [data],
  );
  return (
    <div className="fp-fill">
      {loading && <Loading />}
      <div ref={el} />
    </div>
  );
}

/**
 * PowerPoint's slides, one under the other. Its charts are not drawn (noCharts.ts). Cleaned after drawing
 * as Word is; its pictures and videos are data: and blob: URLs it made itself.
 */
function PptxView({ data }: { data: ArrayBuffer }) {
  const { el, loading } = useDrawn(
    async (target) => {
      const { init } = await import('pptx-preview');
      target.replaceChildren();
      const width = Math.max(240, Math.min(1100, target.clientWidth - 32));
      // Drawn away from the page, as Word is: put in once cleaned.
      const box = document.createElement('div');
      const viewer = init(box, { width, height: Math.round((width * 9) / 16), mode: 'list' });
      // Freed on leaving the file, or on a failure. Its videos and sounds are blob: URLs it never frees (destroy() does
      // not). Each chart's handlers stay in pptx-preview for good (destroy() runs them, never drops them), holding the
      // chart's box, and through it the slides: cut out, the box holds nothing more.
      const free = () => {
        viewer.destroy();
        for (const chart of box.querySelectorAll('.chart-node')) chart.remove();
        for (const u of Object.values(viewer.pptx?.medias ?? {})) if (u.startsWith('blob:')) URL.revokeObjectURL(u);
      };
      try {
        await viewer.preview(data);
        clean(box, { ALLOWED_URI_REGEXP: WITH_BLOB });
      } catch (e) {
        free();
        throw e;
      }
      target.replaceChildren(box);
      return free;
    },
    [data],
  );
  return (
    <div className="fp-fill fp-pptx-box">
      {loading && <Loading />}
      <div ref={el} className="fp-pptx" />
    </div>
  );
}

type Cell = string | number | boolean | Date | null;

/** A sheet as sheetWorker.ts sends it: its first rows and columns, and its whole size (firstCells). */
interface Sheet {
  sheet: string;
  data: Cell[][];
  size?: { rows: number; cols: number };
}

/** The longest a workbook may take to read: then its worker is stopped, and Guard says so. */
const SHEET_MS = 60_000;

/**
 * A workbook's sheets, read once per file (← → back to it reads none again), in a worker: a crafted one would stop
 * the page for a minute. Leaving the file stops the worker (signal). One it cannot read throws: Guard says so.
 */
function SheetView({ nodeId, fileId, data }: { nodeId: string; fileId: string; data: ArrayBuffer }) {
  const read = useQuery({
    queryKey: ['sheets', nodeId, fileId],
    // A copy of the bytes goes: the cache keeps them for ← → back.
    queryFn: ({ signal }) =>
      askWorker<Sheet[]>(() => new Worker(new URL('./sheetWorker.ts', import.meta.url), { type: 'module' }), { data, rows: MAX_ROWS, cols: MAX_COLS } satisfies SheetAsk, signal, SHEET_MS),
    staleTime: Infinity,
    gcTime: 60_000,
    retry: false,
  });
  const [current, setCurrent] = useState(0);

  if (read.isError) throw read.error;
  const sheets = read.data;
  if (!sheets) return <Loading />;
  return (
    <div className="fp-sheet">
      <div className="fp-sheet-grid">
        <Grid rows={sheets[current]?.data ?? []} size={sheets[current]?.size} />
      </div>
      {sheets.length > 1 && (
        <div className="fp-tabs" role="tablist" aria-label="Sheets">
          {sheets.map((s, i) => (
            <button key={`${i}-${s.sheet}`} type="button" role="tab" aria-selected={i === current} className={i === current ? 'on' : ''} onClick={() => setCurrent(i)}>
              {s.sheet}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** At most this many rows of a sheet, a CSV or a zip's list. */
const MAX_ROWS = 2000;

/** Parsed once per file, not on every draw. */
function CsvView({ data }: { data: ArrayBuffer }) {
  const rows = useMemo(() => parseCsv(decodeText(data)), [data]);
  return (
    <div className="fp-sheet">
      <div className="fp-sheet-grid">
        <Grid rows={rows} />
      </div>
      <Truncated data={data} />
    </div>
  );
}

/**
 * One formatter for every cell: one per cell costs seconds on a big sheet. No thousands commas: a year, an ID or a
 * phone number shows as typed (2026, not 2,026), as Excel shows it.
 */
const NUMBER = new Intl.NumberFormat('en-US', { maximumFractionDigits: 10, useGrouping: false });

const showCell = (v: Cell): string => {
  if (v === null || v === undefined) return '';
  if (v instanceof Date) return cellDate(v);
  if (typeof v === 'number') return NUMBER.format(v);
  if (typeof v === 'boolean') return v ? 'TRUE' : 'FALSE';
  return String(v);
};

/** At most this many columns of a sheet or a CSV: one line of a crafted CSV can hold a million. */
const MAX_COLS = 200;

/**
 * A spreadsheet-like table: column letters on top, row numbers on the left, both sticky. `size`: the whole sheet's,
 * when `rows` holds only its first rows and columns (a sheet from sheetWorker.ts).
 */
function Grid({ rows, size }: { rows: Cell[][]; size?: { rows: number; cols: number } }) {
  const shown = rows.slice(0, MAX_ROWS);
  const widest = size?.cols ?? shown.reduce((n, r) => Math.max(n, r.length), 1);
  const total = size?.rows ?? rows.length;
  const cols = Math.min(widest, MAX_COLS);
  return (
    <div className="fp-grid">
      <table>
        <thead>
          <tr>
            <th className="fp-corner" />
            {Array.from({ length: cols }, (_, c) => (
              <th key={c}>{columnName(c)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {shown.map((row, r) => (
            <tr key={r}>
              <th>{r + 1}</th>
              {Array.from({ length: cols }, (_, c) => {
                const v = row[c] ?? null;
                return (
                  <td key={c} className={`${typeof v === 'number' ? 'num' : ''}${r === 0 ? ' first' : ''}`.trim() || undefined}>
                    {showCell(v)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {total > MAX_ROWS && (
        <p className="fp-note">
          Showing the first {MAX_ROWS.toLocaleString('en-US')} of {total.toLocaleString('en-US')} rows.
        </p>
      )}
      {widest > MAX_COLS && (
        <p className="fp-note">
          Showing the first {MAX_COLS} of {widest.toLocaleString('en-US')} columns.
        </p>
      )}
    </div>
  );
}

/** Markdown as formatted text: marked, then DOMPurify. Formatting only: no styles, classes, forms or ids. */
function MarkdownView({ data }: { data: ArrayBuffer }) {
  const { el, loading } = useDrawn(
    async (target) => {
      const { marked } = await import('marked');
      const raw = await marked.parse(decodeText(data), { gfm: true, breaks: true });
      // The file may hold raw HTML, and it lands in this page.
      const doc = DOMPurify.sanitize(raw, {
        USE_PROFILES: { html: true },
        // Not input: a task list's box, made read-only below.
        FORBID_TAGS: FORBID_TAGS.filter((t) => t !== 'input'),
        FORBID_ATTR: ['style', 'class', 'id', 'name', ...FORBID_ATTR],
        RETURN_DOM_FRAGMENT: true,
      });
      // The only input Markdown makes is a task-list box. It is read-only here.
      doc.querySelectorAll('input').forEach((input) => {
        if (input.type === 'checkbox') input.disabled = true;
        else input.remove();
      });
      // A picture on another site: it learns nothing of this page. One on this site: not loaded (keepSafeSources).
      doc.querySelectorAll('img').forEach((img) => img.setAttribute('referrerpolicy', 'no-referrer'));
      keepSafeLinks(doc);
      keepSafeSources(doc);
      target.replaceChildren(doc);
    },
    [data],
  );
  return (
    <div className="fp-fill fp-paper">
      {loading && <Loading />}
      <article ref={el} className="fp-md" />
      <Truncated data={data} />
    </div>
  );
}

/** The files and folders inside a zip, as a tree with sizes. Only the zip's list is downloaded. */
function ZipView({ url, size, failed }: { url: string; size: number; failed: (why: string) => ReactNode }) {
  // No retry: a list that is not a zip stays so. Leaving the file stops the requests (signal).
  const list = useQuery({ queryKey: ['zip', url], queryFn: ({ signal }) => listZip(url, size, signal), staleTime: Infinity, retry: false });
  // The tree's first MAX_ROWS rows and what it holds: once per list, not on every draw.
  const shown = useMemo(() => {
    if (!list.data) return null;
    const tree = zipTree(list.data);
    const rows: { node: ZipNode; depth: number }[] = [];
    const walk = (nodes: ZipNode[], depth: number) => {
      for (const node of nodes) {
        if (rows.length >= MAX_ROWS) return;
        rows.push({ node, depth });
        if (node.children) walk(node.children, depth + 1);
      }
    };
    walk(tree.nodes, 0);
    // Each file and each folder is a row.
    return { rows, summary: zipSummary(tree), total: tree.files + tree.folders };
  }, [list.data]);
  if (list.isError) return <>{failed(errorText(list.error))}</>;
  if (!list.data || !shown) return <Loading />;
  const { rows, summary, total } = shown;

  return (
    <div className="fp-fill fp-paper">
      <div className="fp-zip">
        <p className="fp-note">{rows.length ? summary : 'The zip is empty.'}</p>
        <ul>
          {/* Keyed by place: the rows are made once per list, and a node keeps no path (zipTree). */}
          {rows.map(({ node, depth }, i) => (
            <li key={i} className={node.children ? 'dir' : undefined} style={{ paddingLeft: 8 + depth * 18 }}>
              <span className="fp-zip-ic" aria-hidden="true">
                {node.children ? '📁' : extOf(node.name) || '·'}
              </span>
              <span className="fp-zip-name">{node.name}</span>
              {!node.children && <span className="fp-zip-size">{fmtSize(node.size)}</span>}
            </li>
          ))}
        </ul>
        {total > rows.length && (
          <p className="fp-note">
            Showing the first {rows.length.toLocaleString('en-US')} of {total.toLocaleString('en-US')} files and folders.
          </p>
        )}
      </div>
    </div>
  );
}

/** Text as written: HTML, XML and code too. Never drawn as HTML. Decoded once per file, not on every draw. */
function TextView({ data }: { data: ArrayBuffer }) {
  const text = useMemo(() => decodeText(data), [data]);
  return (
    <div className="fp-fill fp-paper">
      <pre className="fp-text">{text}</pre>
      <Truncated data={data} />
    </div>
  );
}
